import { z } from "zod";
import { OutputAssetSchema } from "./assets";
import { AgentWorkflowRunTaskStatus } from "./agents";
import { AgentWorkflowRunWaitTimerStatusEnum } from "./agentWorkflowRunWaitTimers";
import { PublicUserSchema } from "./users";
import { AgentTaskSchema } from "./tasks";

/** Maximum number of file assets that can be attached to a single message. */
export const MAX_MESSAGE_ASSETS = 20;

export const AGENT_SDK_THREAD_STATUS_VALUES = [
  "ACTIVE",
  "COMPLETED",
  "FAILED",
  "INTERRUPTED",
] as const;

export const AgentSdkThreadStatusEnum = z.enum(AGENT_SDK_THREAD_STATUS_VALUES);

export type AgentSdkThreadStatus = z.infer<typeof AgentSdkThreadStatusEnum>;

export const AGENT_SDK_THREAD_STATUS = AgentSdkThreadStatusEnum.enum;

export const MESSAGE_SCOPE_VALUES = ["EXTERNAL", "INTERNAL", "USER"] as const;
export const MessageScopeEnum = z.enum(MESSAGE_SCOPE_VALUES);
export type MessageScope = z.infer<typeof MessageScopeEnum>;
export const MESSAGE_SCOPE = MessageScopeEnum.enum;

/** Sentiment grades the sentiment sweep assigns to a human-authored user message. */
export const MESSAGE_SENTIMENT_VALUES = [
  "positive",
  "neutral",
  "negative",
] as const;
/** Zod enum of the sentiment grades. */
export const MessageSentimentEnum = z.enum(MESSAGE_SENTIMENT_VALUES);
/** Sentiment grade of a user message. */
export type MessageSentiment = z.infer<typeof MessageSentimentEnum>;
/** Named sentiment grade constants (e.g. `MESSAGE_SENTIMENT.negative`). */
export const MESSAGE_SENTIMENT = MessageSentimentEnum.enum;

export const THREAD_VISIBILITY_VALUES = ["ORGANIZATION", "PRIVATE"] as const;
export const ThreadVisibilityEnum = z.enum(THREAD_VISIBILITY_VALUES);
export type ThreadVisibility = z.infer<typeof ThreadVisibilityEnum>;
export const THREAD_VISIBILITY = ThreadVisibilityEnum.enum;

export const AgentSdkThreadMetadataSchema = z
  .object({
    opportunityActivityLastBackfilledMessageId: z.string().optional(),
    agentSdkTriggerId: z.string().optional(),
    triggerId: z.string().optional(),
    createdFromThreadId: z.string().optional(),
    rootThreadId: z.string().optional(),
    createdThreadDepth: z.number().int().optional(),
  })
  .catchall(z.unknown());

export type AgentSdkThreadMetadata = z.infer<
  typeof AgentSdkThreadMetadataSchema
>;

export const ContentBlockSchema = z.object({
  type: z.enum(["text", "image", "document", "asset"]),
  text: z.string().optional(),
  image_url: z.string().optional(),
  document_url: z.string().optional(),
  asset_id: z.string().optional(),
  detail: z.enum(["auto", "high", "low"]).optional(),
});

export type ContentBlock = z.infer<typeof ContentBlockSchema>;

export const InputMessageSchema = z.object({
  role: z.enum(["user", "system", "developer"]),
  content: z.union([z.string(), z.array(ContentBlockSchema)]),
  type: z.literal("message"),
});

export type InputMessage = z.infer<typeof InputMessageSchema>;

export const OutputMessageSchema = z.object({
  role: z.literal("assistant"),
  content: z.union([z.string(), z.array(ContentBlockSchema)]),
  type: z.literal("message"),
});

export type OutputMessage = z.infer<typeof OutputMessageSchema>;

export const ReasoningMessageSchema = z.object({
  providerId: z.string(),
  type: z.literal("reasoning"),
  content: z.array(z.object({ text: z.string() })),
  encrypted_content: z.string().optional(),
  modelId: z.string().optional(),
});

export type ReasoningMessage<ModelId extends string = string> = Omit<
  z.infer<typeof ReasoningMessageSchema>,
  "modelId"
> & {
  modelId?: ModelId;
};

export const ToolCallMessageSchema = z.object({
  type: z.literal("tool_call"),
  call_id: z.string(),
  name: z.string(),
  arguments: z.record(z.string(), z.unknown()),
  // OpenAI tool search loads discovered tools into a namespace; the namespace
  // must be round-tripped on the function_call item or replay fails with a
  // "Missing namespace" 400.
  namespace: z.string().optional(),
});

export type ToolCallMessage = Omit<
  z.infer<typeof ToolCallMessageSchema>,
  "arguments"
> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  arguments: Record<string, any>;
};

export const ToolCallResultMessageSchema = z.object({
  type: z.literal("tool_call_result"),
  name: z.string(),
  call_id: z.string(),
  result: z.union([z.string(), z.array(ContentBlockSchema)]),
});

export type ToolCallResultMessage = z.infer<typeof ToolCallResultMessageSchema>;

export const CompactionSummaryMessageSchema = z.object({
  type: z.literal("compaction_summary"),
  content: z.string(),
});

export type CompactionSummaryMessage = z.infer<
  typeof CompactionSummaryMessageSchema
>;

/**
 * A tool discovered by a tool search, normalized across providers. Providers
 * differ in how much they return: Anthropic yields only the tool name (the full
 * definition is expanded from the request's tool list), while OpenAI returns
 * the full definition. Every field beyond `name` is therefore optional.
 */
export const DiscoveredToolSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  parameters: z.record(z.string(), z.unknown()).optional(),
  strict: z.boolean().optional(),
  deferLoading: z.boolean().optional(),
});

export type DiscoveredTool = z.infer<typeof DiscoveredToolSchema>;

/**
 * How a tool-search query is expressed. `regex` and `bm25` are the Anthropic
 * search variants; `paths` and `goal` are the OpenAI search modes.
 */
export const TOOL_SEARCH_QUERY_KIND_VALUES = [
  "regex",
  "bm25",
  "paths",
  "goal",
] as const;
export const ToolSearchQueryKindEnum = z.enum(TOOL_SEARCH_QUERY_KIND_VALUES);
export type ToolSearchQueryKind = z.infer<typeof ToolSearchQueryKindEnum>;
export const TOOL_SEARCH_QUERY_KIND = ToolSearchQueryKindEnum.enum;

/**
 * Records a tool-search request issued by the model, normalized into a
 * provider-neutral shape (the union of Anthropic `server_tool_use` and OpenAI
 * `tool_search_call`). Each provider maps its raw block to and from this shape
 * so the search can be replayed to the model without re-running it.
 */
export const ToolSearchCallMessageSchema = z.object({
  type: z.literal("tool_search_call"),
  callId: z.string().nullable(),
  queryKind: ToolSearchQueryKindEnum,
  query: z.array(z.string()),
  modelId: z.string().optional(),
});

export type ToolSearchCallMessage = z.infer<typeof ToolSearchCallMessageSchema>;

/**
 * Records the tools discovered by a tool search, normalized into a
 * provider-neutral shape (the union of Anthropic `tool_search_tool_result` and
 * OpenAI `tool_search_output`). Holds the superset of fields both providers
 * expose so the discovered tools can be replayed to either provider without
 * re-searching.
 */
export const ToolSearchOutputMessageSchema = z.object({
  type: z.literal("tool_search_output"),
  callId: z.string().nullable(),
  tools: z.array(DiscoveredToolSchema),
  error: z
    .object({ code: z.string(), message: z.string().optional() })
    .optional(),
  modelId: z.string().optional(),
});

export type ToolSearchOutputMessage = z.infer<
  typeof ToolSearchOutputMessageSchema
>;

export const ResponsesInputMessageSchema = z.union([
  InputMessageSchema,
  OutputMessageSchema,
  ReasoningMessageSchema,
  ToolCallMessageSchema,
  ToolCallResultMessageSchema,
  CompactionSummaryMessageSchema,
  ToolSearchCallMessageSchema,
  ToolSearchOutputMessageSchema,
]);

export type ResponsesInputMessage<ModelId extends string = string> =
  | InputMessage
  | OutputMessage
  | ReasoningMessage<ModelId>
  | ToolCallMessage
  | ToolCallResultMessage
  | CompactionSummaryMessage
  | ToolSearchCallMessage
  | ToolSearchOutputMessage;

export type ResponsesOutputMessage<ModelId extends string = string> =
  | OutputMessage
  | ToolCallMessage
  | ReasoningMessage<ModelId>
  | ToolSearchCallMessage
  | ToolSearchOutputMessage;

export const AgentSdkThreadSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  userId: z.string().nullable(),
  agentId: z.string(),
  name: z.string().nullable(),
  metadata: AgentSdkThreadMetadataSchema,
  opportunityId: z.string().nullable(),
  status: AgentSdkThreadStatusEnum,
  agentWorkTime: z.number(),
  stopRequested: z.boolean(),
  sandboxId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  pendingTaskCount: z.number().optional(),
  hasActiveWaitTimer: z.boolean().optional(),
  visibility: ThreadVisibilityEnum.optional(),
  /** Whether the requesting user can open and read this thread's content. */
  accessible: z.boolean().optional(),
});

export type AgentSdkThread = z.infer<typeof AgentSdkThreadSchema>;

// Message Screening

export const ScreeningVerdictSchema = z.enum(["ALLOW", "BLOCK", "FLAG"]);
export type ScreeningVerdict = z.infer<typeof ScreeningVerdictSchema>;
export const ScreeningVerdictEnum = ScreeningVerdictSchema.enum;

export const BaseScreeningCategorySchema = z.enum([
  "CLEAN",
  "MALFORMED_RESPONSE",
]);
export const BaseScreeningCategoryEnum = BaseScreeningCategorySchema.enum;

export const InboundScreeningCategorySchema = z.enum([
  "PROMPT_INJECTION",
  "SOCIAL_ENGINEERING",
  "IMPERSONATION",
  "DATA_EXTRACTION",
  "ENCODED_PAYLOAD",
  ...BaseScreeningCategorySchema.options,
]);
export type InboundScreeningCategory = z.infer<
  typeof InboundScreeningCategorySchema
>;
export const InboundScreeningCategoryEnum =
  InboundScreeningCategorySchema.enum;

export const OutboundScreeningCategorySchema = z.enum([
  "MARGIN_LEAK",
  "COST_LEAK",
  "CONFIDENTIAL_DATA",
  ...BaseScreeningCategorySchema.options,
]);
export type OutboundScreeningCategory = z.infer<
  typeof OutboundScreeningCategorySchema
>;
export const OutboundScreeningCategoryEnum =
  OutboundScreeningCategorySchema.enum;

export const ScreeningCategorySchema = z.union([
  InboundScreeningCategorySchema,
  OutboundScreeningCategorySchema,
]);
export type ScreeningCategory = z.infer<typeof ScreeningCategorySchema>;
export const ScreeningCategoryEnum = {
  ...InboundScreeningCategoryEnum,
  ...OutboundScreeningCategoryEnum,
};

export const InboundScreeningResultSchema = z.object({
  verdict: ScreeningVerdictSchema,
  reason: z.string(),
  category: InboundScreeningCategorySchema,
  originalVerdict: ScreeningVerdictSchema,
});
export type InboundScreeningResult = z.infer<
  typeof InboundScreeningResultSchema
>;

export const OutboundScreeningResultSchema = z.object({
  verdict: ScreeningVerdictSchema,
  reason: z.string(),
  category: OutboundScreeningCategorySchema,
  originalVerdict: ScreeningVerdictSchema,
});
export type OutboundScreeningResult = z.infer<
  typeof OutboundScreeningResultSchema
>;

export const ScreeningResultSchema = z.object({
  verdict: ScreeningVerdictSchema,
  reason: z.string(),
  category: ScreeningCategorySchema,
  originalVerdict: ScreeningVerdictSchema,
});
export type ScreeningResult = InboundScreeningResult | OutboundScreeningResult;

/**
 * Snapshot of what the user had open in Item Editor V2 when they sent a chat
 * message, so the agent can resolve references like "this item" or "this
 * drawing" against what was actually on screen.
 */
export const ItemEditorViewContextSchema = z.object({
  screen: z.literal("itemEditor"),
  itemId: z.string(),
  itemName: z.string().optional(),
  activeTab: z.enum(["drawings", "briefing", "vault", "evidence"]).optional(),
  activePanel: z.enum(["details", "bom"]).optional(),
  drawingAssetId: z.string().optional(),
  drawingFileName: z.string().optional(),
  vaultFileId: z.string().optional(),
  vaultFileName: z.string().optional(),
  evidenceMessageId: z.string().optional(),
  evidenceSubject: z.string().optional(),
});

export type ItemEditorViewContext = z.infer<typeof ItemEditorViewContextSchema>;

/**
 * Per-message view context: which screen the user was on when the message was
 * sent, discriminated by `screen` so more screens can be added later.
 */
export const UserViewContextSchema = z.discriminatedUnion("screen", [
  ItemEditorViewContextSchema,
]);

export type UserViewContext = z.infer<typeof UserViewContextSchema>;

export const UserMessageMetadataSchema = z.object({
  type: z.string(),
  label: z.string().optional(),
  senderMetadata: z
    .object({
      email: z.string().nullable().optional(),
      phoneNumber: z.string().nullable().optional(),
      name: z.string().nullable().optional(),
      userId: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  fields: z.record(z.string(), z.string()),
  scope: MessageScopeEnum.optional(),
  screeningResult: InboundScreeningResultSchema.optional(),
});

export type UserMessageMetadata = z.infer<typeof UserMessageMetadataSchema>;

export const AgentSdkMessageSchema = z.object({
  id: z.string(),
  threadId: z.string(),
  message: ResponsesInputMessageSchema,
  assetIds: z.array(z.string()).nullable(),
  assets: z.array(OutputAssetSchema).optional(),
  agentId: z.string(),
  parentToolCallId: z.string().nullable(),
  userId: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  /**
   * Monotonic per-thread ordering key. Always set on messages returned by the
   * API (the DB mapper populates it); optional so in-memory/test literals need
   * not supply it. Used as the pagination cursor by the thread UI.
   */
  threadSequence: z.number().optional(),
  isTurnFinal: z.boolean().optional(),
  task: z
    .object({
      id: z.string(),
      status: z.nativeEnum(AgentWorkflowRunTaskStatus),
      description: z.string().nullable().optional(),
      statusChangedByUserId: z.string().nullable().optional(),
      assignees: z
        .array(
          z.object({
            id: z.string(),
            assigneeUserId: z.string(),
            firstName: z.string().nullable().optional(),
            lastName: z.string().nullable().optional(),
            email: z.string().optional(),
          }),
        )
        .optional(),
    })
    .optional(),
  waitTimer: z
    .object({
      id: z.string(),
      status: AgentWorkflowRunWaitTimerStatusEnum,
      waitUntil: z.coerce.date().nullable(),
    })
    .optional(),
  messageMetadata: UserMessageMetadataSchema.nullable().optional(),
  viewContext: UserViewContextSchema.nullable().optional(),
  user: PublicUserSchema.optional(),
});

export type AgentSdkMessage<ModelId extends string = string> = Omit<
  z.infer<typeof AgentSdkMessageSchema>,
  "message"
> & {
  message: ResponsesInputMessage<ModelId>;
  messageMetadata?: UserMessageMetadata | null;
};

export const AgentSdkThreadsResponseSchema = z.object({
  threads: z.array(AgentSdkThreadSchema),
});

export const AgentSdkThreadResponseSchema = z.object({
  thread: AgentSdkThreadSchema,
});

/**
 * Summary of a non-cancelled wait timer returned alongside thread messages so
 * the thread UI can render a countdown bar.
 */
export const AgentSdkThreadWaitTimerSchema = z.object({
  id: z.string(),
  subject: z.string().nullable(),
  description: z.string().nullable(),
  status: AgentWorkflowRunWaitTimerStatusEnum,
  waitUntil: z.coerce.date().nullable(),
});
export type AgentSdkThreadWaitTimer = z.infer<
  typeof AgentSdkThreadWaitTimerSchema
>;

export const AgentSdkMessagesResponseSchema = z.object({
  messages: z.array(AgentSdkMessageSchema),
  openTasks: z.array(AgentTaskSchema).default([]),
  waitTimers: z.array(AgentSdkThreadWaitTimerSchema).default([]),
  /**
   * True when an older slot exists before this page. Pages are sized by slots,
   * so this tracks turn history, not raw rows.
   */
  hasOlder: z.boolean().default(false),
});

/**
 * Default page size for the paginated thread-messages endpoint, counted in
 * *slots*: a top-level user message, or the turn-final response that closed a
 * run. Twenty slots is ten full turns. Tool calls, reasoning, sub-agent
 * messages, and intermediate responses are returned with their turn but don't
 * consume a slot. A tool_result whose tool_call sits on an older, not-yet-loaded
 * page orphans to root — cosmetic, and self-heals once the older page loads.
 */
export const DEFAULT_MESSAGE_PAGE_SIZE = 20;

/**
 * Query params for `GET /standard-agent/threads/:threadId/messages`. Two mutually
 * exclusive cursor modes over the monotonic `threadSequence`:
 * - `beforeSequence` (exclusive): load the page of older turns before a cursor.
 * - `sinceSequence` (inclusive): delta poll — everything at/after a cursor, so a
 *   still-streaming last message is re-fetched and upserted rather than missed.
 * With neither cursor, the latest `limit` slots are returned. `limit` counts
 * slots, not raw message rows.
 */
export const GetMessagesQuerySchema = z.object({
  limit: z
    .string()
    .optional()
    .transform((val) => (val ? parseInt(val, 10) : DEFAULT_MESSAGE_PAGE_SIZE)),
  beforeSequence: z
    .string()
    .optional()
    .transform((val) => (val ? parseInt(val, 10) : undefined)),
  sinceSequence: z
    .string()
    .optional()
    .transform((val) => (val ? parseInt(val, 10) : undefined)),
});

export type GetMessagesQuery = z.infer<typeof GetMessagesQuerySchema>;

export const AgentSdkQueueRunResponseSchema = z.object({
  threadId: z.string(),
  eventId: z.string(),
});

export const AgentSdkAddMessageResponseSchema = z.object({
  eventId: z.string(),
});

export const ListThreadsQuerySchema = z.object({
  limit: z
    .string()
    .optional()
    .transform((val) => (val ? parseInt(val, 10) : undefined)),
  offset: z
    .string()
    .optional()
    .transform((val) => (val ? parseInt(val, 10) : undefined)),
  userId: z.string().optional(),
  opportunityId: z.string().optional(),
  search: z.string().optional(),
});

export type ListThreadsQuery = z.infer<typeof ListThreadsQuerySchema>;

// Thread visibility schemas

export const SetThreadVisibilitySchema = z.object({
  visibility: ThreadVisibilityEnum,
});
export type SetThreadVisibilityInput = z.infer<
  typeof SetThreadVisibilitySchema
>;

export const AddThreadVisibilityUserSchema = z.object({
  userId: z.string(),
});
export type AddThreadVisibilityUserInput = z.infer<
  typeof AddThreadVisibilityUserSchema
>;

export const ThreadVisibilityResponseSchema = z.object({
  threadId: z.string(),
  visibility: ThreadVisibilityEnum,
  users: z.array(
    z.object({
      id: z.string(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      email: z.string(),
    }),
  ),
});
export type ThreadVisibilityResponse = z.infer<
  typeof ThreadVisibilityResponseSchema
>;

/** Tool name constant for the send_rich_message agent tool. */
export const SEND_RICH_MESSAGE_TOOL_NAME = "send_rich_message";

export const RichMessagePartSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("text"),
    text: z.string().describe("Markdown-formatted text content"),
  }),
  z.object({
    type: z.literal("assets"),
    asset_ids: z
      .array(z.string())
      .describe(
        "Asset IDs to display inline. These must come from tool call results (e.g. from sandbox read_file, search results, or action multimodal outputs).",
      ),
  }),
]);

export const SendRichMessageInputSchema = z.object({
  parts: z
    .array(RichMessagePartSchema)
    .describe(
      "Ordered array of content parts to display. Text parts are rendered as markdown, asset parts are rendered as inline file/image previews.",
    ),
});

export type RichMessagePart = z.infer<typeof RichMessagePartSchema>;
export type SendRichMessageInput = z.infer<typeof SendRichMessageInputSchema>;
